import React, { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { X, ArrowRight, ArrowLeft, Handshake, Storefront, Lightning, Sparkle } from "@phosphor-icons/react";

/**
 * WelcomeLottie — guide onboarding animé pour le 1er login commerçant.
 * 4 slides avec animation Lottie "welcome chaleureux" (mains qui saluent / étoiles).
 * Persistance localStorage par user_id ; relançable via event `mc68:welcome-replay`.
 */
const STEPS = [
  {
    key: "welcome",
    icon: <Handshake size={22} weight="duotone" />,
    title: "Bienvenue sur Mon Commerce 68",
    body:
      "Vous rejoignez la vitrine premium des entreprises du Haut-Rhin. Ce petit guide de 40 secondes vous montre l'essentiel pour faire briller votre commerce.",
  },
  {
    key: "claim",
    icon: <Storefront size={22} weight="duotone" />,
    title: "Réclamez votre fiche",
    body:
      "Retrouvez votre commerce dans l'annuaire et cliquez sur « C'est ma fiche ». Elle bascule instantanément sous votre contrôle, gratuitement.",
  },
  {
    key: "trial",
    icon: <Lightning size={22} weight="duotone" />,
    title: "14 jours Premium offerts",
    body:
      "Sans carte bancaire. Activez l'essai depuis votre tableau de bord : photos illimitées, IA, badge partenaire, boost visibilité — tout de suite.",
  },
  {
    key: "shine",
    icon: <Sparkle size={22} weight="duotone" />,
    title: "Personnalisez votre profil",
    body:
      "Logo, horaires, description, photos. Une fiche complète capte 3× plus de contacts. On vous guide pas à pas dans le wizard d'onboarding.",
  },
];

export default function WelcomeLottie({ open, onClose, onAction }) {
  const [step, setStep] = useState(0);
  const [animData, setAnimData] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    if (animData) return;
    fetch("/lottie/welcome.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && setAnimData(j))
      .catch(() => {});
  }, [open, animData]);

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const next = () => {
    if (isLast) {
      onClose?.();
      onAction?.();
    } else setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div
      data-testid="welcome-lottie-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        data-testid="welcome-lottie-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-3xl bg-card ring-1 ring-border shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
      >
        <button
          data-testid="welcome-lottie-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center rounded-full bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground transition"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        {/* Lottie header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/10 h-56 flex items-center justify-center">
          {animData ? (
            <Lottie
              animationData={animData}
              loop
              autoplay
              style={{ height: 200, width: 200 }}
              rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
            />
          ) : (
            <div className="w-40 h-40 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkle size={64} weight="duotone" className="text-primary" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-8 pt-6 pb-8">
          <div className="flex items-center gap-2 text-primary mb-3" data-testid={`welcome-lottie-step-${current.key}`}>
            {current.icon}
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold">
              Étape {step + 1} / {STEPS.length}
            </span>
          </div>
          <h2 className="text-2xl font-semibold leading-tight text-foreground mb-3">
            {current.title}
          </h2>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            {current.body}
          </p>

          {/* Dots */}
          <div className="flex items-center gap-2 mt-6 mb-4">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                data-testid={`welcome-lottie-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-8 bg-primary" : "w-2 bg-border hover:bg-muted-foreground/40"
                }`}
                aria-label={`Étape ${i + 1}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              data-testid="welcome-lottie-skip"
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-foreground transition"
            >
              Passer le guide
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  data-testid="welcome-lottie-prev"
                  onClick={prev}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-muted hover:bg-muted/80 text-foreground text-sm font-medium transition"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
              )}
              <button
                data-testid="welcome-lottie-next"
                onClick={next}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition shadow-sm"
              >
                {isLast ? "C'est parti !" : "Suivant"}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
